<template>
  <div>
    <v-dialog
      v-model="dialog"
      max-width="530"
      scrollable
      content-class="rounded-xl"
      :persistent="disableButtons"
    >
      <v-card>
        <v-card-title>
          <h5 class="mb-0">Exportar ingressos</h5>
        </v-card-title>
        <v-card-text class="pa-4">
          <h6 class="mb-0 mt-2">Ingresso selecionado</h6>

          <ticket-exporter-list-item
            class="pr-2"
            :ticket="tickets[currentTicketIndex]"
            :disable="disableButtons"
            :shared="shared.includes(tickets[currentTicketIndex].id)"
            :downloaded="downloaded.includes(tickets[currentTicketIndex].id)"
            :loadingShare="loadingShare == tickets[currentTicketIndex].id"
            :loadingExport="loadingExport == tickets[currentTicketIndex].id"
            :index="currentTicketIndex"
            :filesCache="filesCache"
            @share="sharePDF(tickets[currentTicketIndex])"
            @download="downloadPDF(tickets[currentTicketIndex])"
          />

          <template v-if="tickets.length > 1">
            <v-divider class="my-4" />
            <h6 class="mb-0 mt-3">Outros ingressos desse evento</h6>
            <v-list>
              <template v-for="(ticket, index) in tickets">
                <ticket-exporter-list-item
                  v-if="index !== currentTicketIndex"
                  :ticket="ticket"
                  :disable="disableButtons"
                  :shared="shared.includes(ticket.id)"
                  :downloaded="downloaded.includes(ticket.id)"
                  :loadingShare="loadingShare == ticket.id"
                  :loadingExport="loadingExport == ticket.id"
                  :index="index"
                  :filesCache="filesCache"
                  @share="sharePDF(ticket)"
                  @download="downloadPDF(ticket)"
                />
                <v-divider v-if="index !== currentTicketIndex"></v-divider>
              </template>
            </v-list>
          </template>
        </v-card-text>
        <v-progress-linear
          :height="downloadingAll ? 10 : 0"
          :value="(downloadingAllProgress / tickets.length) * 100"
        />
        <v-card-actions class="flex-wrap">
          <v-btn text @click="close" :disabled="disableButtons">Fechar</v-btn>
          <v-spacer />
          <v-btn
            v-if="tickets.length > 1"
            small
            depressed
            color="primary"
            @click="downloadAll"
            :loading="downloadingAll"
            :disabled="disableButtons"
          >
            Baixar todos
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <export-view v-if="party" :party="party" ref="exportPdf" name="exportPdf" />
    <ticket-exporter-share-modal ref="shareModal" name="shareModal" />
  </div>
</template>

<script>
import JSZip from "jszip";
import ExportView from "./ExportView.vue";
import TicketExporterActionBtn from "./TicketExporterActionBtn.vue";
import TicketExporterShareModal from "./TicketExporterShareModal.vue";
import TicketExporterListItem from "./TicketExporterListItem.vue";
export default {
  name: "TicketExportModal",
  components: {
    ExportView,
    TicketExporterActionBtn,
    TicketExporterShareModal,
    TicketExporterListItem,
  },
  data() {
    return {
      dialog: false,
      loadingExport: false,
      loadingShare: false,
      downloaded: [],
      shared: [],
      downloadingAll: false,
      downloadingAllProgress: 0,
      filesCache: {},
    };
  },
  methods: {
    async generatePDF(ticket, ignoreShare = false) {
      const ticketId = ticket.id;
      if (this.filesCache[ticketId]) return this.filesCache[ticketId].file;

      var shareName = true;
      const isCourtesy = ticket.Payment?.paymentType === "COURTESY";
      if (!ignoreShare && !isCourtesy) {
        shareName = await this.$refs.shareModal.open(ticket);
        if (!shareName) throw new Error("User canceled");
      }

      const blob = await this.$refs.exportPdf.generatePDF({
        ticket,
        shareName,
      });
      this.filesCache[ticketId] = {
        file: blob,
        shareName: shareName === true ? null : shareName,
      };
      return blob;
    },
    async downloadPDF(ticket) {
      try {
        this.loadingExport = ticket.id;
        const blob = await this.generatePDF(ticket);
        if (!blob) return;
        const url = URL.createObjectURL(blob);

        const a = document.createElement("a");
        a.href = url;
        a.download = this.getFileName(ticket);
        a.click();
        a.remove();
        await new Promise((resolve) => setTimeout(resolve, 200));
        this.downloaded = Array.from(new Set([...this.downloaded, ticket.id]));
      } catch (error) {
        console.error(error);
      } finally {
        this.loadingExport = false;
      }
    },
    async sharePDF(ticket) {
      try {
        this.loadingShare = ticket.id;
        const blob = await this.generatePDF(ticket);
        const fileName = this.getFileName(ticket);
        const file = new File([blob], fileName, { type: "application/pdf" });
        this.shared = Array.from(new Set([...this.shared, ticket.id]));
        window.navigator.share({
          text: fileName,
          files: [file],
        });
      } catch (error) {
        console.error(error);
      } finally {
        this.loadingShare = false;
      }
    },
    async downloadAll() {
      try {
        this.downloadingAll = true;
        this.downloadingAllProgress = 0;
        const zip = new JSZip();
        for (const ticket of this.tickets) {
          const blob = await this.generatePDF(ticket, true);
          if (!blob) return;
          zip.file(this.getFileName(ticket), blob);
          this.downloaded = Array.from(
            new Set([...this.downloaded, ticket.id])
          );
          this.downloadingAllProgress++;
          await new Promise((resolve) => setTimeout(resolve, 100));
        }
        const zipBlob = await zip.generateAsync({ type: "blob" });
        const url = URL.createObjectURL(zipBlob);

        const a = document.createElement("a");
        a.href = url;
        a.download = `Ingressos ${this.party?.name}.zip`;
        a.click();
        a.remove();

        this.downloadingAll = false;
      } catch (error) {
        console.error(error);
      } finally {
        this.downloadingAll = false;
      }
    },
    getFileName(ticket) {
      return `${this.party?.name} - ${
        ticket.TicketBlock?.TicketGroup?.name
      } - ${ticket.TicketBlock?.name} - ${ticket.id.slice(0, 8)}.pdf`;
    },
    open() {
      this.dialog = true;
    },
    close() {
      this.dialog = false;
    },
  },
  computed: {
    disableButtons() {
      return !!(this.downloadingAll || this.loadingExport || this.loadingShare);
    },
  },
  mounted() {
    this.$parent.$on("openTicketExportModal", this.open);
  },
  beforeDestroy() {
    this.$parent.$off("openTicketExportModal", this.open);
  },
  props: {
    party: {
      type: Object,
      required: true,
    },
    tickets: {
      type: Array,
      required: true,
    },
    currentTicketIndex: {
      type: Number,
      required: true,
    },
  },
};
</script>
